/** Read-only wording for the existing source-authorized review endpoint. */
export interface TermsReviewItem {
  job_id: string
  status: string
  review_state: string
  current: boolean
  publication_blocked: boolean
  coverage?: string[]
  scope?: { holder_name?: string | null; instrument_name?: string | null }
  attempts: {
    validation?: { reasons?: { code: string; detail?: string }[] }
    error_code?: string | null
  }[]
}

export interface TermsReviewRow {
  id: string
  title: string
  scope: string | null
  reasons: string[]
  message: string
}

const REASONS: Record<string, string> = {
  citation_document: 'A cited document was not available to this review.',
  citation_version: 'A citation refers to a different version of its source.',
  citation_page: 'A cited page was not available to this review.',
  citation_quote: "The reader's quotation does not match its cited page.",
  replacement_source_mismatch: 'The agreement replacing an earlier version is not established.',
  governing_date_unknown: 'The date when the agreement takes effect is not established.',
  standing_period_unresolved: 'The agreement governing this balance is not established.',
  source_coverage: 'The review could not establish all required source information.',
  review_attempt_failed: 'The reader could not complete this review.',
}

export function termsReviewRows(items: TermsReviewItem[]): TermsReviewRow[] {
  return items.flatMap((item) => {
    if (item.coverage?.length) {
      return [
        {
          id: item.job_id,
          title: 'Terms review access is limited',
          scope: null,
          reasons: [],
          message: 'Some sources are outside your current document access.',
        },
      ]
    }
    if (!item.current) {
      return [
        {
          id: item.job_id,
          title: 'Earlier terms review',
          scope: null,
          reasons: [],
          message:
            'The source information has changed. This earlier result is not a current decision.',
        },
      ]
    }
    if (!item.publication_blocked) return []
    if (item.status === 'complete' && item.review_state !== 'needs_review') return []
    const working = ['pending', 'running'].includes(item.status)
    const latest = item.attempts[item.attempts.length - 1]
    const reasons = [
      ...new Set(
        (latest?.validation?.reasons ?? []).map((reason) => {
          if (REASONS[reason.code]) return REASONS[reason.code]
          if (
            ['unresolved_terms', 'unresolved_cash'].includes(reason.code) &&
            reason.detail?.trim()
          ) {
            return reason.detail
          }
          return 'A source-supported interpretation is still required.'
        })
      ),
    ]
    return [
      {
        id: item.job_id,
        title: working ? 'Terms review in progress' : 'Terms need review',
        scope:
          [item.scope?.holder_name, item.scope?.instrument_name].filter(Boolean).join(' · ') ||
          null,
        reasons: working ? [] : reasons,
        message: working
          ? 'The document is waiting for its terms review to complete.'
          : 'This review cannot release financial proposals. Check the agreement and obtain any missing source documents before continuing.',
      },
    ]
  })
}
